import { createFileRoute, Link } from "@tanstack/react-router";
import { SafeImage } from "@/components/site/SafeImage";
import { ArticleSidebar } from "@/components/site/Sidebar";
import { ShareBar } from "@/components/site/ShareBar";
import { autoLinkText } from "@/lib/auto-link";
import { Clock, Calendar, ChevronLeft } from "lucide-react";
import { Breadcrumbs, buildBreadcrumbLd } from "@/components/site/Breadcrumbs";
import { TableOfContents } from "@/components/site/TableOfContents";
import { KeyTakeaways } from "@/components/site/KeyTakeaways";
import { ArticleEndMatter } from "@/components/site/ArticleEndMatter";
import { EditorNote } from "@/components/site/EditorNote";
import { summaryPlacement, notePlacement } from "@/lib/article-variation";
import { buildArticleLd, buildFaqLd, ensureFaq } from "@/lib/article-schema";
import { buildToc } from "@/lib/article-toc";
import { slugify } from "@/lib/slugify";
import { makeSeoTitle } from "@/lib/seo-title";
import { socialImageMeta } from "@/lib/social";
import { makeSeoDescription } from "@/lib/seo-description";
import { getGeneralArticle } from "@/data/general-articles";

const SLUG = "currency-hedging-expats-2026";
const article = getGeneralArticle(SLUG)!;

export const Route = createFileRoute("/currency-hedging-expats-2026")({
  head: () => {
    const a = article;
    const url = `https://lifewestway.com/${a.slug}`;
    const seoTitle = makeSeoTitle(a.seoTitle ?? a.title);
    return {
      meta: [
        { title: seoTitle },
        { name: "description", content: makeSeoDescription(a.description) },
        { property: "og:title", content: seoTitle },
        { property: "og:description", content: makeSeoDescription(a.description) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        ...socialImageMeta({ image: a.heroImage, alt: a.title }),
        { property: "og:site_name", content: "LifeWestWay" },
        { property: "article:published_time", content: a.publishedAt },
        { property: "article:modified_time", content: a.updatedAt },
        { property: "article:section", content: a.category },
        { name: "twitter:title", content: seoTitle },
        { name: "twitter:description", content: makeSeoDescription(a.description) },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildArticleLd({
              url,
              headline: a.title,
              description: a.description,
              image: a.heroImage,
              datePublished: a.publishedAt,
              dateModified: a.updatedAt,
              section: a.category,
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbLd([
              { label: "Home", href: "/" },
              { label: "Cross-Border Wealth", href: "/cross-border-wealth" },
              { label: a.title, href: `/${a.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildFaqLd(ensureFaq(a.faq, { title: a.title, category: a.category, updatedAt: a.updatedAt }), url)),
        },
      ],
    };
  },
  component: ArticlePage,
});

function ArticlePage() {
  const a = article;

  return (
    <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-navy text-white">
        <SafeImage width={1200} height={675}
          eager
          src={a.heroImage}
          alt={`${a.title} — LifeWestWay cross-border wealth guide for all six countries`}
          className="absolute inset-0 -z-10 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <Link
            title="Back to the Cross-Border Wealth hub"
            to="/cross-border-wealth"
            className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-accent hover:underline"
          >
            <ChevronLeft className="h-3 w-3" aria-hidden /> Cross-Border Wealth
          </Link>
          <div className="mt-4 flex items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">
              {a.category}
            </span>
          </div>
          <h1 className="mt-4 max-w-4xl font-display text-3xl font-black leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {a.title}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/80 sm:text-lg">{a.description}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-white/70">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" aria-hidden /> Updated {a.updatedAt}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" aria-hidden /> {a.readMinutes} min read
            </span>
          </div>
        </div>
      </section>

      <Breadcrumbs
        items={[
          { label: "Home", href: "/" },
          { label: "Cross-Border Wealth", href: "/cross-border-wealth" },
          { label: a.title },
        ]}
      />

      {/* Body */}
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mt-8 grid gap-10 lg:grid-cols-12">
          <article className="prose-article lg:col-span-8">
            <TableOfContents items={buildToc(a.sections)} />
            {summaryPlacement(a.slug) === "beforeIntro" ? (
              <>
                <KeyTakeaways slug={a.slug} intro={a.intro} sections={a.sections} faq={a.faq} />
                <p className="lead text-lg leading-relaxed text-foreground">{a.intro}</p>
              </>
            ) : (
              <>
                <p className="lead text-lg leading-relaxed text-foreground">{a.intro}</p>
                <KeyTakeaways slug={a.slug} intro={a.intro} sections={a.sections} faq={a.faq} />
              </>
            )}
            {notePlacement(a.slug) === "afterSummary" ? <EditorNote slug={a.slug} href={`/${a.slug}`} category={a.category} /> : null}

            {a.sections.map((s, i) => {
              if (s.type === "h2") return <h2 key={i} id={slugify(s.text ?? "")}>{s.text}</h2>;
              if (s.type === "h3") return <h3 key={i} id={slugify(s.text ?? "")}>{s.text}</h3>;
              if (s.type === "h4") return <h4 key={i}>{s.text}</h4>;
              if (s.type === "p") return <p key={i}>{autoLinkText(s.text ?? "", `/${a.slug}`)}</p>;
              if (s.type === "ul")
                return (
                  <ul key={i}>
                    {s.items?.map((it, j) => <li key={j}>{it}</li>)}
                  </ul>
                );
              if (s.type === "ol")
                return (
                  <ol key={i}>
                    {s.items?.map((it, j) => <li key={j}>{it}</li>)}
                  </ol>
                );
              return null;
            })}

            {notePlacement(a.slug) === "beforeFaq" ? <EditorNote slug={a.slug} href={`/${a.slug}`} category={a.category} /> : null}
            <ArticleEndMatter slug={a.slug} faq={ensureFaq(a.faq, { title: a.title, category: a.category, updatedAt: a.updatedAt })} />
          </article>

          <div className="lg:col-span-4">
            <ArticleSidebar />
          </div>
        </div>
      </div>
      <ShareBar title={a.title} />
    </>
  );
}
